import lod_ from "lodash";
import { Chip, Dialog, DialogActions, DialogContent, Divider, Icon, Tooltip } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import { useState } from "react";
import { t } from "i18next";
import FiltersDndList from "./FiltersDndList";
import MDTypography from "components/Basics/MDTypography";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import { useMaterialUIController } from "context";

/**
 * Filters dialog component
 * This component allows users to add and manage filters for a resource.
 * It displays a list of filters, allows adding new filters from a dictionary,
 * and provides options to save or cancel changes.
 * @param {Object} props - The properties for the FiltersDialog component.
 * @param {Object} props.filtersDialog - The state of the filters dialog (open/closed).
 * @param {Function} props.setFiltersDialog - Function to update the filters dialog state
 * @param {Object} props.dictionary - The dictionary object containing filter definitions.
 * @param {Array} props.filters - The initial list of filters to display.
 * @param {Function} props.onSave - Callback function to handle saving filters.
 * @param {string} props.page - The current page route used to identify filters in the store.
 * @returns {JSX.Element} The rendered FiltersDialog component.
 */
const FiltersDialog = ({
	filtersDialog,
	setFiltersDialog,
	dictionary = {},
	filters: resourcefilters = [],
	onSave = () => {},
	page
}) => {
	const dispatch = useDispatch();
	const profile = useSelector(selectCurrentProfile);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	const [filters, setFilters] = useState(resourcefilters);
	const [addFilterAnchorEl, setAddFilterAnchorEl] = useState(null);

	/**
	 * Add a new filter to the list
	 * @param {string} fullDictionaryPath - The path of the filter in the dictionary
	 * @returns {void}
	 */
	const addNewFilter = fullDictionaryPath => {
		let dictionaryCode = fullDictionaryPath.split(".")[0];

		const dictionaryOfElement = lod_.get(dictionary, dictionaryCode);
		const source = dictionaryOfElement?.source;

		let path = lod_.cloneDeep(fullDictionaryPath);
		if (path.split(".").length > 1) {
			path = path.split(".").slice(1).join(".");
		}

		// Try to get the item in the dictionary
		const dictionaryItem = lod_.get(
			dictionaryOfElement?.items || {},
			path.replaceAll(".", ".items."),
			{}
		);

		if (lod_.isEmpty(dictionaryItem) && source !== "special") {
			return;
		}

		setAddFilterAnchorEl(null);
		let same = filters.find(f => f.path === path);
		if (!same) {
			let filter = {
				dictionary: dictionaryCode,
				path,
				source: source === "special" ? source : "dictionary"
			};

			if (source === "special") {
				filter.type = dictionaryOfElement.type;
			}

			setFilters(prev => [...filters, filter]);
		}
	};

	/**
	 * When editing a filter
	 * @param {number} index - The index of the filter in the list
	 * @param {object} edit - The editions to apply
	 */
	const onEdit = (index, edit) => {
		let clonedFilters = lod_.cloneDeep(filters);
		let item = clonedFilters[index];
		item = lod_.merge(item, edit);
		clonedFilters[index] = item;
		setFilters(clonedFilters);
	};

	/**
	 * Remove a link from a filter
	 * @param {number} itemItem - The index of the filter in the list
	 * @param {number} linkIndex - The index of the link in the filter
	 */
	const removeLink = (itemItem, linkIndex) => {
		let clonedFilters = lod_.cloneDeep(filters);
		let item = clonedFilters[itemItem];
		item.links.splice(linkIndex, 1);
		clonedFilters[itemItem] = item;
		setFilters(clonedFilters);
	};

	/**
	 * Add a link to a filter
	 * @param {object} item - The filter to link
	 * @param {number} index - The index of the filter in the list
	 * @param {string} fullDictionaryPath - The path of the filter in the dictionary
	 */
	const linkItem = (item, index, fullDictionaryPath) => {
		let dictionaryCode = fullDictionaryPath.split(".")[0];

		const dictionaryItem = lod_.get(dictionary, dictionaryCode);
		const source = dictionaryItem?.source;

		let path = lod_.cloneDeep(fullDictionaryPath);
		if (path.split(".").length > 1) {
			path = path.split(".").slice(1).join(".");
		}

		setAddFilterAnchorEl(null);

		let link = {
			dictionary: dictionaryCode,
			path,
			source: source === "special" ? source : "dictionary"
		};

		item.links = item.links || [];

		let same = item.links.find(l => `${l.dictionaryCode}.${l.path}` === fullDictionaryPath);
		if (!same) {
			item.links.push(link);
		}

		let clonedFilters = lod_.cloneDeep(filters);
		clonedFilters[index] = item;
		setFilters(clonedFilters);
	};

	const handleClose = () => setFiltersDialog({ open: false });

	return (
		<Dialog
			fullWidth
			maxWidth="xl"
			open={filtersDialog.open}
			onClose={handleClose}
			slotProps={{
				paper: {
					sx: ({ palette, boxShadows }) => ({
						borderRadius: 3,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						width: "100%",
						height: "90vh",
						maxHeight: "90vh",
						backgroundColor: darkMode ? palette.background.card : palette.white.main,
						boxShadow: boxShadows.xxl
					})
				}
			}}
		>
			{/* ─────────────── Header ─────────────── */}
			<MDBox
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				px={3}
				py={2.25}
				sx={({ palette, functions: { linearGradient, rgba } }) => ({
					background: linearGradient(
						rgba(palette.info.main, darkMode ? 0.25 : 0.12),
						rgba(palette.info.main, darkMode ? 0.08 : 0.03)
					)
				})}
			>
				<MDBox display="flex" alignItems="center" gap={1.75}>
					<MDBox
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={({ palette, functions: { linearGradient } }) => ({
							width: 46,
							height: 46,
							borderRadius: "12px",
							color: palette.white.main,
							background: linearGradient(palette.info.main, palette.info.focus),
							boxShadow: `0 6px 16px ${darkMode ? "rgba(0,0,0,0.4)" : "rgba(26,115,232,0.35)"}`
						})}
					>
						<Icon fontSize="medium">filter_list</Icon>
					</MDBox>
					<MDBox display="flex" flexDirection="column">
						<MDBox display="flex" alignItems="center" gap={1}>
							<MDTypography variant="h5" fontWeight="bold">
								{t("SETTINGS.FILTERS.addFilters")}
							</MDTypography>
						</MDBox>
						<MDTypography variant="button" color="text" fontWeight="regular">
							{t("SETTINGS.FILTERS.items")}
						</MDTypography>
					</MDBox>
				</MDBox>

				<Tooltip title={t("SETTINGS.cancel")} placement="top" arrow>
					<MDButton
						variant="text"
						color={darkMode ? "white" : "dark"}
						iconOnly
						circular
						size="medium"
						onClick={handleClose}
					>
						<Icon>close</Icon>
					</MDButton>
				</Tooltip>
			</MDBox>

			<Divider sx={{ m: 0 }} />

			<DialogContent
				sx={{ px: 3, py: 2.5, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
			>
				<FiltersDndList
					dictionary={dictionary}
					items={filters}
					onAddFilter={e => setAddFilterAnchorEl(e.currentTarget)}
					onChange={newList => {
						setFilters(prev => newList);
					}}
					deleteItem={index => {
						setFilters(prev => {
							let newList = [...prev];
							newList.splice(index, 1);
							return newList;
						});
					}}
					linkItem={linkItem}
					removeLink={removeLink}
					onEdit={onEdit}
				/>

				{filters.length > 0 && (
					<MDBox display="flex" justifyContent="end" mt={1.5}>
						<MDButton
							variant="gradient"
							color="info"
							size="small"
							onClick={e => setAddFilterAnchorEl(e.currentTarget)}
						>
							<Icon>add</Icon>&nbsp;{t("SETTINGS.add")}
						</MDButton>
					</MDBox>
				)}

				<DictionaryMenu
					dictionary={dictionary}
					anchorEl={addFilterAnchorEl}
					handleInsertText={addNewFilter}
					handleClose={() => setAddFilterAnchorEl(null)}
					clickableLevels={false}
				/>
			</DialogContent>

			<Divider sx={{ m: 0 }} />

			<DialogActions sx={{ px: 3, py: 2 }}>
				<MDButton variant="outlined" color="secondary" onClick={handleClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton variant="gradient" color="info" onClick={() => onSave(filters)}>
					<Icon>check</Icon>&nbsp;{t("SETTINGS.save")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default FiltersDialog;
